// apolo/src/components/code/sessionWorkspace/buildChatFn.js
// Fix 1 (code-tab-to-10) — extract the cloud/local chatFn builder from runAgent.
// Pure factory: returns the chatFn + turnBody pair. No React, no closures over component state.

import { apiFetch, apiUrl, getStoredToken } from '../../../lib/api.js';
import { runCloudTurn } from '../../../lib/cloudStream.js';
import { agentTurnRequest } from '../../../lib/apiContracts.js';
import { asSessionId } from '../../../lib/brandedIds.js';
import { collectWorkerTurnFlags } from '../../../lib/flags/sprintFlags.js';

/**
 * Build the chatFn for the agent loop, based on the route (local/cloud).
 * @param {object} route — { kind, model, isLmStudio, llmAdapterId }
 * @param {string} sessionId
 * @param {object} steering — { sourcesRef }
 * @param {object|null} [spec]
 * @returns {Function} chatFn
 */
export function buildChatFn(route, sessionId, steering, spec = null) {
  if (route.kind === 'local') {
    const ns = typeof window !== 'undefined'
      ? (route.isLmStudio ? window.apolo?.lmstudio : window.apolo?.ollama)
      : null;
    // E3-WS1-T02 — o 3º arg (AbortSignal) chega até o IPC: o preload traduz o abort
    // num invoke 'apolo:llm:abortStream' e o main cancela o fetch em voo. Antes o
    // Stop abandonava a Promise e a geração local seguia órfã até o timeout de 10min.
    return async (params, onDelta, signal) => {
      if (ns?.chatStream) {
        try {
          const r = await ns.chatStream(params, onDelta, signal);
          if (r && r.ok) return r;
        } catch { /* fall through to non-stream */ }
      }
      if (signal?.aborted) return { ok: false, error: 'abortado pelo usuário' };
      return ns?.chat ? ns.chat(params) : { ok: false, error: 'IPC local indisponível (fora do Electron).' };
    };
  }

  // Cloud path
  // harness-live-verify — Worker-side sprint flags (localStorage → turno). null ⇒
  // campo ausente ⇒ body byte-idêntico ao de hoje (net-zero com flags OFF).
  const workerFlags = collectWorkerTurnFlags();
  const turnBody = (msgs, tools) => ({
    messages: msgs,
    tools,
    model: route.model,
    llm_adapter_id: route.llmAdapterId || null,
    steering: steering.sourcesRef.current,
    ...(workerFlags ? { flags: workerFlags } : {}),
    // Speed selector: only present for reasoning models (gated upstream in
    // detectRoute/SessionWorkspace), so the Worker never sends reasoning_effort
    // to a model that would reject it.
    ...(route.reasoningEffort ? { reasoningEffort: route.reasoningEffort } : {}),
    ...(spec ? { spec } : {}),
  });

  return async ({ messages: msgs, tools }, onDelta, signal) => {
    const streamFetch = (sig) => {
      const token = getStoredToken();
      return fetch(apiUrl(`/api/code-ide/sessions/${sessionId}/agent-turn?stream=1`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(turnBody(msgs, tools)),
        signal: sig,
      });
    };

    const nonStreamCall = async (sig) => {
      const req = agentTurnRequest(asSessionId(sessionId), turnBody(msgs, tools));
      const res = await apiFetch(req.path, { method: req.method, body: req.body, signal: sig });
      if (!res.ok) return { ok: false, error: res.error || 'agent-turn falhou' };
      const fb = res.data?.fallback;
      if (fb?.to) {
        const short = (id) => String(id || '').split('/').pop();
        window.dispatchEvent(new CustomEvent('apolo:toast', {
          detail: { kind: 'info', message: `Modelo ${short(fb.from)} indisponível — respondi com ${short(fb.to)}.` },
        }));
      }
      return { ok: true, content: res.data?.content || '', tool_calls: res.data?.tool_calls || [] };
    };

    return runCloudTurn({ streamFetch, nonStreamCall, onDelta, signal });
  };
}
